/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { AbTreeRootNodeImpl } from "abreact/tree/node/AbTreeRootNodeImpl";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SEPARATOR, AbItemSeparator } from "abweb/collection/AbItemSeparator";
import { AbIterable } from "abweb/collection/AbIterable";
import { AbKeySet } from "abweb/collection/AbKeySet";
import { AbSet } from "abweb/collection/AbSet";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { AbAsyncUtils } from "abweb/utils/async/AbAsyncUtils";

/**
 * The data and utility functions that are supported by any tree node.
 */

/**
 * A tree node may either be a root node (which only has children, no value) or a child node, which 
 * has optional children and a guaranteed value.
 */

/**
 * Type for designating different tree events.
 */
export let AbTreeEvent = /*#__PURE__*/function (AbTreeEvent) {
  /**
   * Occurs when the nodes that the tree needs to show changes
   */
  AbTreeEvent[AbTreeEvent["UPDATE"] = 0] = "UPDATE";
  /**
   * Occurs when a node in the tree expands
   */
  AbTreeEvent[AbTreeEvent["EXPAND"] = 1] = "EXPAND";
  /**
   * Occurs when a node in the tree collapses
   */
  AbTreeEvent[AbTreeEvent["COLLAPSE"] = 2] = "COLLAPSE";
  return AbTreeEvent;
}({});

/**
 * Map from tree event types to the callbacks associated with them.
 */

/**
 * Bundle of filter results. Returns the nodes that match the filter (matchedNodes)
 * and the nodes that need to be expanded to show the matched nodes (expandedNodes)
 */

/**
 * Bundle of tristate results. Returns the nodes that are currently selected (valueNodes)
 * and the nodes keys that need to be indeterminate (indeterminateNodeKeys)
 */
/**
 * Collection of utilites for working with trees.
 */
export let AbTreeUtils;
(function (_AbTreeUtils) {
  const isTreeRoot = _AbTreeUtils.isTreeRoot = tree => tree.depth === 0;
  const itemToChild = _AbTreeUtils.itemToChild = (tree, item) => {
    return tree.children.find(child => child !== AB_ITEM_SEPARATOR && TypeUtils.equals(child.value, item, tree.keyFunction));
  };
  /**
   * Utility function to find the child of tree with the given item as its value.
   */
  const itemToCachedChild = (tree, item) => {
    return tree.cachedChildren.find(child => child !== AB_ITEM_SEPARATOR && TypeUtils.equals(child.value, item, tree.keyFunction));
  };

  /**
   * Finds the tree at the given path under the given tree. Will return undefined if not found. Only searches nodes that have been expanded.
   */
  const find = _AbTreeUtils.find = (tree, path) => {
    if (path.length === 0) return isTreeRoot(tree) ? undefined : tree;
    const item = path[0];
    const child = itemToChild(tree, item);
    return child === undefined ? undefined : AbTreeUtils.find(child, path.slice(1));
  };
  const findCached = _AbTreeUtils.findCached = (tree, path) => {
    if (path.length === 0) return isTreeRoot(tree) ? undefined : tree;
    const item = path[0];
    const child = itemToCachedChild(tree, item);
    return child === undefined ? undefined : AbTreeUtils.findCached(child, path.slice(1));
  };
  const getEnrichedPath = _AbTreeUtils.getEnrichedPath = async (tree, path) => {
    if (path.length === 0) return [];
    if (tree.cachedChildren.length === 0 && !isTreeRoot(tree)) await tree.cacheChildren();
    const child = itemToCachedChild(tree, path[0]);
    if (!child) return undefined;
    const restOfEnrichedPath = await getEnrichedPath(child, path.slice(1));
    if (!restOfEnrichedPath) return undefined;
    if (child.value === AB_ITEM_SEPARATOR) return restOfEnrichedPath;
    return [AbEnriched.createMaybe(child.value, child.enrichment), ...restOfEnrichedPath];
  };
  const getCachedEnrichedPath = _AbTreeUtils.getCachedEnrichedPath = (tree, path) => {
    if (path.length === 0) return [];
    const child = itemToCachedChild(tree, path[0]);
    if (!child) return undefined;
    const restOfEnrichedPath = getCachedEnrichedPath(child, path.slice(1));
    if (!restOfEnrichedPath) return undefined;
    if (child.value === AB_ITEM_SEPARATOR) return restOfEnrichedPath;
    return [AbEnriched.createMaybe(child.value, child.enrichment), ...restOfEnrichedPath];
  };
  const getVisibleItems = _AbTreeUtils.getVisibleItems = root => {
    let visibleItems = [];
    for (let i = 0; i < root.size; i++) {
      visibleItems.push(root.itemAt(i));
    }
    return visibleItems;
  };
  const expandPath = _AbTreeUtils.expandPath = (tree, path) => {
    if (path.length === 0) {
      return isTreeRoot(tree) ? undefined : tree.expander?.expand();
    }
    const item = path[0];
    const expandSelfPromise = expandPath(tree, [])?.then(() => {
      const child = itemToChild(tree, item);
      if (!child) return undefined;
      return expandPath(child, path.slice(1));
    });
    if (!expandSelfPromise) {
      const child = itemToChild(tree, item);
      if (!child) return undefined;
      return expandPath(child, path.slice(1));
    }
    return expandSelfPromise;
  };
  const expandPaths = _AbTreeUtils.expandPaths = (tree, paths) => {
    const expandedSet = AbKeySet.from(paths, makePathKeyFunction(tree.keyFunction)).map(path => expandPath(tree, path));
    if (expandedSet.every(expanded => expanded === undefined)) return undefined;
    // eslint-disable-next-line @typescript-eslint/await-thenable
    return Promise.all(expandedSet).then();
  };
  const expandAllArray = trees => {
    const expandedArray = trees.map(tree => expandAll(tree));
    if (expandedArray.every(expanded => expanded === undefined)) return undefined;
    // eslint-disable-next-line @typescript-eslint/await-thenable
    return Promise.all(expandedArray).then();
  };

  /**
   * Expand all nodes in the tree. WARNING: Do not call on a gigantic or heavily asynchronous tree.
   */
  const expandAll = _AbTreeUtils.expandAll = tree => {
    if (tree === AB_ITEM_SEPARATOR) return undefined;
    if (isTreeRoot(tree)) return expandAllArray(tree.children);
    const expandPromise = tree.expander?.expand()?.then(() => expandAllArray(tree.children));
    return expandPromise ?? expandAllArray(tree.children);
  };
  const collapseAll = _AbTreeUtils.collapseAll = tree => {
    tree.cachedChildren.forEach(child => {
      if (child !== AB_ITEM_SEPARATOR) collapseAll(child);
    });
    if (!isTreeRoot(tree)) tree.collapse();
  };
  const collapsePath = _AbTreeUtils.collapsePath = (tree, path) => {
    const child = AbTreeUtils.find(tree, path);
    if (!child) return;
    let curr = child;
    while (!isTreeRoot(curr)) {
      const next = curr.parent;
      curr.collapse();
      curr = next;
    }
  };
  const collapsePaths = _AbTreeUtils.collapsePaths = (tree, paths) => {
    AbKeySet.from(paths, makePathKeyFunction(tree.keyFunction)).forEach(path => collapsePath(tree, path));
  };
  const collapse = _AbTreeUtils.collapse = (tree, path) => {
    AbTreeUtils.find(tree, path)?.collapse();
  };
  const toggle = _AbTreeUtils.toggle = (tree, path) => {
    const child = AbTreeUtils.find(tree, path);
    if (!child) return undefined;
    if (child.size <= 1) return child.expander?.expand();
    child.collapse();
    return undefined;
  };
  const cachePath = _AbTreeUtils.cachePath = (tree, path) => {
    if (path.length === 0) {
      return isTreeRoot(tree) ? undefined : tree.cacheChildren();
    }
    const item = path[0];
    const cacheSelfPromise = cachePath(tree, [])?.then(() => {
      const child = itemToCachedChild(tree, item);
      if (!child) return undefined;
      return cachePath(child, path.slice(1));
    });
    if (!cacheSelfPromise) {
      const child = itemToCachedChild(tree, item);
      if (!child) return undefined;
      return cachePath(child, path.slice(1));
    }
    return cacheSelfPromise;
  };
  const cachePaths = _AbTreeUtils.cachePaths = (tree, paths) => {
    const cachedArray = paths.map(path => cachePath(tree, path));
    if (cachedArray.every(cachePromise => cachePromise === undefined)) return undefined;
    // eslint-disable-next-line @typescript-eslint/await-thenable
    return Promise.all(cachedArray).then();
  };
  const cacheAllArray = trees => {
    const cachedArray = trees.map(tree => cacheAll(tree));
    if (cachedArray.every(cachePromise => cachePromise === undefined)) return undefined;
    // eslint-disable-next-line @typescript-eslint/await-thenable
    return Promise.all(cachedArray).then();
  };

  /**
   * Cache the children for every node in the tree. WARNING: Do not use in large or heavily asynchronous tree.
   * Used for trees that need to live entirely in memory to function, such as a tree for filtering or searching.
   */
  const cacheAll = _AbTreeUtils.cacheAll = tree => {
    if (tree === AB_ITEM_SEPARATOR) return undefined;
    if (isTreeRoot(tree)) return cacheAllArray(tree.cachedChildren);
    const cachePromise = tree.cacheChildren()?.then(() => cacheAllArray(tree.cachedChildren));
    return cachePromise ?? cacheAllArray(tree.cachedChildren);
  };
  const indexOf = _AbTreeUtils.indexOf = (tree, path) => {
    if (path.length === 0) {
      return isTreeRoot(tree) ? undefined : 0;
    }
    const item = path[0];
    let size = isTreeRoot(tree) ? 0 : 1;
    for (const child of tree.children) {
      if (child !== AB_ITEM_SEPARATOR && TypeUtils.equals(child.value, item, child.keyFunction)) {
        const recursiveIndex = AbTreeUtils.indexOf(child, path.slice(1));
        if (recursiveIndex === undefined) {
          return undefined;
        }
        return size + recursiveIndex;
      }
      size += child === AB_ITEM_SEPARATOR ? 1 : child.size;
    }
    return undefined;
  };
  const indicesOfChildren = _AbTreeUtils.indicesOfChildren = (tree, parentPath) => {
    const parentNode = AbTreeUtils.find(tree, parentPath);
    if (!parentNode) return undefined;
    const parentIndex = AbTreeUtils.indexOf(tree, parentPath);
    if (parentIndex === undefined) return undefined;
    const indices = [];
    let size = 1;
    for (const child of parentNode.children) {
      if (child !== AB_ITEM_SEPARATOR) indices.push(parentIndex + size);
      size += child === AB_ITEM_SEPARATOR ? 1 : child.size;
    }
    if (indices.length === 0) return undefined;
    return indices;
  };
  const filter = _AbTreeUtils.filter = (tree, filterFunction) => {
    const children = tree.children;
    if (!children) return {
      value: tree.value
    };
    return {
      get children() {
        if (!children) return [];
        return AbAsyncUtils.mapItem(children, value => value.filter(child => filterFunction(child.value)).map(child => AbTreeUtils.filter(child, filterFunction)));
      },
      value: tree.value
    };
  };
  const filterRoots = _AbTreeUtils.filterRoots = (roots, filterFunction) => {
    return AbAsyncUtils.mapItem(roots, value => value.filter(root => filterFunction(root.value)).map(root => AbTreeUtils.filter(root, filterFunction)));
  };
  const map = _AbTreeUtils.map = (tree, mapFunction, parent) => {
    const children = tree.children;
    if (!children) return {
      value: mapFunction(tree.value, parent)
    };
    const mapped = {
      get children() {
        if (!children) return undefined;
        return AbAsyncUtils.mapItem(children, syncChildren => syncChildren.map(child => AbTreeUtils.map(child, mapFunction, mapped)));
      },
      value: mapFunction(tree.value, parent)
    };
    return mapped;
  };
  const mapRoots = _AbTreeUtils.mapRoots = (roots, mapFunction) => {
    return AbAsyncUtils.mapItem(roots, syncRoots => syncRoots.map(root => AbTreeUtils.map(root, mapFunction)));
  };
  const getExpandedPaths = _AbTreeUtils.getExpandedPaths = tree => {
    const children = tree.children;
    if (children.length === 0) return []; // No children means not expanded
    const childPaths = children.reduce((prev, curr) => {
      if (curr === AB_ITEM_SEPARATOR) return [];
      const childExpanded = getExpandedPaths(curr);
      if (childExpanded.length === 0) return prev;
      return prev.concat(childExpanded);
    }, []).map(path => isTreeRoot(tree) ? path : [tree.value, ...path]); // Concatenate all sub-expanded paths, and prepend this node
    if (childPaths.length > 0) return childPaths; // If paths found through children, return
    if (isTreeRoot(tree)) return []; // If no paths found and is root, return
    return [[tree.value]]; // At the very least, this is expanded.
  };
  const getFilteredPaths = _AbTreeUtils.getFilteredPaths = (tree, filterFunction) => {
    const childPaths = tree.cachedChildren.reduce((prev, curr) => {
      if (curr === AB_ITEM_SEPARATOR) return prev;
      const childFiltered = getFilteredPaths(curr, filterFunction);
      if (childFiltered.length === 0) return prev;
      return prev.concat(childFiltered);
    }, []);
    if (isTreeRoot(tree)) return childPaths;
    if (tree.value === AB_ITEM_SEPARATOR) return [];
    if (!filterFunction(tree.value, tree.enrichment)) return childPaths;
    return [tree.enrichedPath].concat(childPaths);
  };
  const filterPathsToSets = _AbTreeUtils.filterPathsToSets = (tree, paths) => {
    const expandSet = AbSet.from();
    const matchSet = AbSet.from();
    paths.forEach(path => {
      let currNode = tree;
      for (const [index, maybeEnrichedItem] of path.entries()) {
        const childNode = itemToCachedChild(currNode, AbEnriched.getItem(maybeEnrichedItem));
        if (!childNode) break;
        currNode = childNode;
        if (index === path.length - 1) {
          matchSet.add(childNode);
        } else {
          expandSet.add(childNode);
        }
      }
    });
    return {
      expandedNodes: expandSet,
      matchedNodes: matchSet
    };
  };
  const getFilterSets = _AbTreeUtils.getFilterSets = (tree, filterFunction) => {
    const expandSet = AbSet.from();
    const matchSet = AbSet.from();
    tree.cachedChildren.forEach(child => {
      if (child === AB_ITEM_SEPARATOR) return;
      getFilterSetsHelper(child, filterFunction, expandSet, matchSet);
    });
    return {
      expandedNodes: expandSet,
      matchedNodes: matchSet
    };
  };
  const getFilterSetsHelper = (tree, filterFunction, expandSet, matchSet) => {
    let hasMatch = false;
    if (filterFunction(tree.value, tree.enrichment)) {
      matchSet.add(tree);
      hasMatch = true;
    }
    if (tree.enrichment?.disableSearchWhenCollapsed && (!tree.expander || !tree.expander.isExpanded())) return hasMatch;
    tree.cachedChildren.forEach(child => {
      if (child === AB_ITEM_SEPARATOR) return;
      const childHasMatch = getFilterSetsHelper(child, filterFunction, expandSet, matchSet);
      if (childHasMatch) {
        expandSet.add(tree);
        hasMatch = true;
      }
    });
    return hasMatch;
  };

  /**
   * Used for converting AbTreeType.UNIQUE values into their corresponding nodes.
   */
  const itemsToNodes = _AbTreeUtils.itemsToNodes = (tree, initialValue) => {
    const childNodes = AbSet.from();
    const recurse = currNode => {
      currNode.cachedChildren.forEach(child => {
        if (child === AB_ITEM_SEPARATOR) return;
        if (initialValue.has(child.value)) childNodes.add(child);
        recurse(child);
      });
    };
    recurse(tree);
    return childNodes;
  };
  const getTristateSets = _AbTreeUtils.getTristateSets = (tree, initialValue, nodeToKey) => {
    const indeterminateSet = AbSet.from();
    const valueSet = AbSet.from();
    // Sort to do shortest paths first to do more short-cutting (if two paths are selected and one is a sub-path of the other, the longer path is irrelevant)
    AbIterable.from(initialValue).map(AbEnriched.getItem).toArray().sort((p1, p2) => p1.length - p2.length).forEach(maybeEnrichedPath => {
      const path = AbEnriched.getItem(maybeEnrichedPath);
      let currNode = tree;
      for (const [index, pathPart] of path.entries()) {
        const child = itemToCachedChild(currNode, pathPart);
        if (!child) break;
        if (index === path.length - 1) {
          valueSet.add(child);
          indeterminateSet.delete(child);
        } else {
          // If valueSet has child, can stop looking
          if (valueSet.has(child)) return;
          indeterminateSet.add(child);
        }
        currNode = child;
      }
    });

    // Copy stored to avoid modifying the set we are iterating over
    const valueSetCopy = AbSet.from(valueSet);
    // Sort so deepest are at the end to be popped first
    const valueArray = AbIterable.from(valueSet).toArray().sort((c1, c2) => c1.depth - c2.depth);
    while (true) {
      const deepestVal = valueArray.pop();
      // if none left, break
      if (!deepestVal) break;
      const parent = deepestVal.parent;
      // if deepest is under root, no more parents
      if (isTreeRoot(parent)) break;
      if (parent.cachedChildren.every(child => child === AB_ITEM_SEPARATOR || valueSet.has(child))) {
        valueSet.add(parent);
        indeterminateSet.delete(parent);
        // insert the parent into the "deepest" array at the correct place.
        if (valueArray.length === 0) {
          valueArray.push(parent);
        } else {
          for (let i = valueArray.length - 1; i >= 0; i--) {
            const otherChild = valueArray[i];
            if (otherChild.depth <= parent.depth) {
              valueArray.splice(i + 1, 0, parent);
              break;
            }
          }
        }
      }
    }
    const addAllDescendants = node => {
      node.cachedChildren.forEach(child => {
        if (child === AB_ITEM_SEPARATOR) return;
        if (valueSet.has(child)) return;
        valueSet.add(child);
        indeterminateSet.delete(child);
        addAllDescendants(child);
      });
    };
    valueSetCopy.forEach(addAllDescendants);
    return {
      indeterminateNodeKeys: indeterminateSet.map(nodeToKey),
      valueNodes: valueSet
    };
  };
  const getIndependentIndeterminateNodes = _AbTreeUtils.getIndependentIndeterminateNodes = (tree, initialValue, nodeToKey) => {
    const indeterminateSet = AbSet.from();
    const valueSet = AbSet.from();
    initialValue.forEach(maybeEnrichedPath => {
      const path = AbEnriched.getItem(maybeEnrichedPath);
      let currNode = tree;
      for (const [index, pathPart] of path.entries()) {
        const child = itemToCachedChild(currNode, pathPart);
        if (!child) break;
        if (index === path.length - 1) {
          valueSet.add(child);
          indeterminateSet.delete(child);
        } else if (!valueSet.has(child)) {
          indeterminateSet.add(child);
        }
        currNode = child;
      }
    });
    return indeterminateSet.map(nodeToKey);
  };
  const treeLeftArrow = _AbTreeUtils.treeLeftArrow = (treeRoot, focusItem) => {
    if (focusItem === undefined || !focusItem.path) return;
    if (focusItem.expander?.isExpanded()) {
      focusItem.expander.collapse(); // If the current focus is expanded, collapse it.
      return;
    }
    if (focusItem.depth > 1) {
      return AbTreeUtils.indexOf(treeRoot, focusItem.path.slice(0, -1));
    }
  };
  const expandAllSiblings = _AbTreeUtils.expandAllSiblings = (treeRoot, focusItem) => {
    if (!focusItem || !focusItem.path) return;
    const allSiblings = focusItem.depth === 1 ? treeRoot.children : AbTreeUtils.find(treeRoot, focusItem.path.slice(0, -1))?.children;
    if (!allSiblings) return;
    return expandPaths(treeRoot, allSiblings.filter(AbItemSeparator.isNotSeparator).map(sib => sib.path));
  };
  const getAllPaths = _AbTreeUtils.getAllPaths = async data => {
    const children = (typeof data.children === "function" ? await data.children() : data.children) ?? [];
    const childPaths = [[]];
    for (let i = 0; i < children.length; i++) {
      childPaths.push(...(await getAllPaths(children[i])));
    }
    return childPaths.map(path => [data.value, ...path]);
  };
  const createTreeRoot = _AbTreeUtils.createTreeRoot = (data, keyFunction) => {
    return new AbTreeRootNodeImpl(data, keyFunction);
  };
  const makePathKeyFunction = _AbTreeUtils.makePathKeyFunction = keyFunction => {
    return path => {
      return ArrayUtils.safeJoin(path.map(item => "t_" + TypeUtils.itemToKey(AbEnriched.getItem(item), keyFunction)));
    };
  };
  const preprocessTreeWithSeparators = _AbTreeUtils.preprocessTreeWithSeparators = treeData => {
    if (treeData.value === AB_ITEM_SEPARATOR) return AB_ITEM_SEPARATOR;
    return {
      value: treeData.value,
      get children() {
        if (!treeData.children) return undefined;
        return AbAsyncUtils.mapItem(treeData.children, AbTreeUtils.preprocessTreeArrayWithSeparators);
      }
    };
  };
  const preprocessTreeArrayWithSeparators = _AbTreeUtils.preprocessTreeArrayWithSeparators = treeDataArray => {
    return AbItemSeparator.deduplicateSeparators(treeDataArray.map(tree => AbTreeUtils.preprocessTreeWithSeparators(tree)));
  };
  const sort = _AbTreeUtils.sort = (tree, compareFunction) => {
    return {
      get children() {
        if (!tree.children) return [];
        return AbTreeUtils.sortRoots(tree.children, compareFunction);
      },
      value: tree.value
    };
  };
  const sortRoots = _AbTreeUtils.sortRoots = (roots, compareFunction) => {
    return AbAsyncUtils.mapItem(roots, syncRoots => syncRoots.map(root => AbTreeUtils.sort(root, compareFunction)).sort((i1, i2) => compareFunction(i1.value, i2.value)));
  };
  const equal = _AbTreeUtils.equal = async (tree1, tree2, keyFunction) => {
    if (!TypeUtils.equals(AbEnriched.getItem(tree1.value), AbEnriched.getItem(tree2.value), keyFunction)) {
      return false;
    }
    return equalRoots(tree1.children ?? [], tree2.children ?? [], keyFunction);
  };
  const equalRoots = _AbTreeUtils.equalRoots = async (roots1, roots2, keyFunction) => {
    const syncRoots1 = await AbAsyncUtils.resolve(roots1);
    const syncRoots2 = await AbAsyncUtils.resolve(roots2);
    if (syncRoots1.length !== syncRoots2.length) return false;
    for (let i = 0; i < syncRoots1.length; i++) {
      const rootEqual = await equal(syncRoots1[i], syncRoots2[i], keyFunction);
      if (!rootEqual) return false;
    }
    return true;
  };
  /**
   * Utility for turning a tree into a console string. Used for debugging purposes. Will expand all async data.
   */
  const treeToStringHelper = async (tree, itemToString, prefix, isRoot, isLastSibling = false) => {
    const valueString = itemToString?.(AbEnriched.getItem(tree.value)) ?? AbItemUtils.defaultItemDisplayName(tree.value);
    const getPointer = () => {
      if (isRoot) return "";
      return isLastSibling ? "└──" : "├──";
    };
    const myLine = `${prefix}${getPointer()}${valueString}`;
    const children = await AbAsyncUtils.resolve(tree.children);
    if (!children || children.length === 0) return myLine;
    const newPrefix = isRoot ? "" : `${prefix}${isLastSibling ? "   " : "│  "}`;
    const childrenStrings = await Promise.all(children.map((child, index) => treeToStringHelper(child, itemToString, newPrefix, false, index === children.length - 1)));
    return `${myLine}\n${childrenStrings.join("\n")}`;
  };

  /**
   * Utility for turning an array of tree roots into a console string. Used for debugging purposes. Will expand all async data.
   */
  const rootsToString = _AbTreeUtils.rootsToString = async (roots, itemToString) => {
    const syncRoots = await AbAsyncUtils.resolve(roots);
    const treeStrings = await Promise.all(syncRoots.map(root => treeToStringHelper(root, itemToString, "", true)));
    return treeStrings.join("\n");
  };
  const toMenuItem = _AbTreeUtils.toMenuItem = (node, initializer) => {
    if (node === AB_ITEM_SEPARATOR) {
      return AB_ITEM_SEPARATOR;
    }
    const item = AbEnriched.getItem(node.value);
    const enrichment = AbEnriched.getEnrichment(node.value);
    const children = node.children;
    return {
      label: enrichment?.displayName,
      disabled: enrichment?.disabled,
      className: enrichment?.className,
      tooltip: enrichment?.tooltip,
      ...(initializer?.(item, enrichment) ?? []),
      menuItems: children ? toMenuItems(children, initializer) : undefined
    };
  };
  const toMenuItems = _AbTreeUtils.toMenuItems = (roots, initializer) => {
    return AbAsyncUtils.mapItem(roots, syncRoots => {
      return syncRoots.map(root => toMenuItem(root, initializer));
    });
  };
  const toMenuItemsWithPreprocessing = _AbTreeUtils.toMenuItemsWithPreprocessing = (roots, initializer) => {
    return AbAsyncUtils.mapItem(roots, syncRoots => {
      return AbTreeUtils.preprocessTreeArrayWithSeparators(syncRoots).map(root => toMenuItem(root, initializer));
    });
  };
  const preorderTraversalRoots = _AbTreeUtils.preorderTraversalRoots = (roots, visitor) => {
    if (AbAsyncUtils.isAsyncFunc(roots)) {
      return roots().then(children => preorderTraversalArray(children, visitor));
    }
    return preorderTraversalArray(roots, visitor);
  };
  const preorderTraversalArray = (trees, visitor) => {
    let currValue = undefined;
    trees.forEach(tree => {
      if (currValue === undefined) currValue = preorderTraversal(tree, visitor);else currValue = currValue.then(() => preorderTraversal(tree, visitor));
    });
    return currValue;
  };

  /**
   * Expand all nodes in the tree. Do not call on a gigantic or heavily asynchronous tree.
   */
  const preorderTraversal = _AbTreeUtils.preorderTraversal = (tree, visitor) => {
    visitor(tree.value);
    if (!tree.children) return undefined;
    if (AbAsyncUtils.isAsyncFunc(tree.children)) {
      return tree.children().then(children => preorderTraversalArray(children, visitor));
    } else {
      return preorderTraversalArray(tree.children, visitor);
    }
  };
})(AbTreeUtils || (AbTreeUtils = {}));
/**
 * Utility interface which asserts separators cannot have children.
 */
//# sourceMappingURL=AbTreeUtils.js.map
